import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import './LianLianKan.css';

const ROWS = 6;
const COLS = 10;
const SYMBOLS = [
  '🐱', '🐶', '🐼', '🐸', '🦊', '🐻', '🐹', '🐨', '🐯', '🦁',
  '🐷', '🐮', '🐤', '🐙', '🦋', '🌸', '🍓', '🍇', '🍩', '🍣',
  '🥐', '🥑', '🍀', '🌈', '⭐️', '⚽️', '🎲', '🎧', '🎮', '🧩',
];
const defaultMessage = 'Pick two identical tiles that can be linked with at most two turns.';

const shuffle = (input) => {
  const arr = [...input];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

const createBoard = () => {
  const pairCount = (ROWS * COLS) / 2;
  const pool = [];
  for (let i = 0; i < pairCount; i += 1) {
    const symbol = SYMBOLS[i % SYMBOLS.length];
    pool.push(symbol, symbol);
  }

  const shuffled = shuffle(pool);
  const board = [];
  let idx = 0;

  for (let r = 0; r < ROWS; r += 1) {
    const row = [];
    for (let c = 0; c < COLS; c += 1) {
      row.push({
        id: `tile-${r}-${c}-${shuffled[idx]}-${idx}`,
        value: shuffled[idx],
      });
      idx += 1;
    }
    board.push(row);
  }

  return board;
};

const directions = [
  { dr: 1, dc: 0 },
  { dr: -1, dc: 0 },
  { dr: 0, dc: 1 },
  { dr: 0, dc: -1 },
];

const findLinkPath = (board, first, second) => {
  if (!first || !second) return null;
  if (first.row === second.row && first.col === second.col) return null;

  const rows = board.length;
  const cols = board[0].length;
  const extendedRows = rows + 2;
  const extendedCols = cols + 2;

  const occupancy = Array.from({ length: extendedRows }, (_, r) =>
    Array.from({ length: extendedCols }, (_, c) => {
      if (r === 0 || c === 0 || r === extendedRows - 1 || c === extendedCols - 1) return 0;
      return board[r - 1][c - 1] ? 1 : 0;
    }),
  );

  const start = { r: first.row + 1, c: first.col + 1 };
  const target = { r: second.row + 1, c: second.col + 1 };

  occupancy[start.r][start.c] = 0;
  occupancy[target.r][target.c] = 0;

  const visited = Array.from({ length: extendedRows }, () =>
    Array.from({ length: extendedCols }, () => Array(4).fill(Infinity)),
  );
  const parents = Array.from({ length: extendedRows }, () =>
    Array.from({ length: extendedCols }, () => Array(4).fill(null)),
  );

  const queue = [{ r: start.r, c: start.c, dir: -1, turns: 0 }];
  let found = null;

  while (queue.length) {
    const current = queue.shift();
    if (current.turns > 2) continue;
    if (current.r === target.r && current.c === target.c && current.dir !== -1) {
      found = current;
      break;
    }

    for (let d = 0; d < directions.length; d += 1) {
      const nextR = current.r + directions[d].dr;
      const nextC = current.c + directions[d].dc;
      if (nextR < 0 || nextC < 0 || nextR >= extendedRows || nextC >= extendedCols) continue;

      const nextTurns = current.dir === -1 || current.dir === d ? current.turns : current.turns + 1;
      if (nextTurns > 2) continue;

      if (occupancy[nextR][nextC] && !(nextR === target.r && nextC === target.c)) continue;
      if (visited[nextR][nextC][d] <= nextTurns) continue;

      visited[nextR][nextC][d] = nextTurns;
      parents[nextR][nextC][d] = { r: current.r, c: current.c, dir: current.dir };
      queue.push({ r: nextR, c: nextC, dir: d, turns: nextTurns });
    }
  }

  if (!found) return null;

  const path = [];
  let node = found;

  while (node) {
    path.push({ r: node.r, c: node.c });
    if (node.dir === -1) break;
    const prev = parents[node.r][node.c][node.dir];
    if (!prev) break;
    node = { r: prev.r, c: prev.c, dir: prev.dir };
  }
  return path.reverse();
};

const findLinkablePair = (board) => {
  const positionsByValue = new Map();
  board.forEach((row, rIdx) => {
    row.forEach((cell, cIdx) => {
      if (!cell) return;
      const arr = positionsByValue.get(cell.value) || [];
      arr.push({ row: rIdx, col: cIdx });
      positionsByValue.set(cell.value, arr);
    });
  });

  for (const positions of positionsByValue.values()) {
    for (let i = 0; i < positions.length; i += 1) {
      for (let j = i + 1; j < positions.length; j += 1) {
        const path = findLinkPath(board, positions[i], positions[j]);
        if (path) {
          return { first: positions[i], second: positions[j], path };
        }
      }
    }
  }
  return null;
};

const hasAvailableMove = (board) => Boolean(findLinkablePair(board));

const shuffleExistingTiles = (board) => {
  const coords = [];
  const values = [];

  board.forEach((row, rIdx) => {
    row.forEach((cell, cIdx) => {
      if (cell) {
        coords.push({ row: rIdx, col: cIdx });
        values.push(cell.value);
      }
    });
  });

  if (!coords.length) return board;

  const shuffledValues = shuffle(values);
  const nextBoard = board.map((row) =>
    row.map((cell) => (cell ? { ...cell } : null)),
  );

  coords.forEach((pos, idx) => {
    const value = shuffledValues[idx];
    nextBoard[pos.row][pos.col] = {
      id: `tile-${pos.row}-${pos.col}-${value}-${Math.random().toString(36).slice(2, 7)}`,
      value,
    };
  });

  return nextBoard;
};

const ensurePlayableBoard = (board) => {
  const remaining = board.flat().filter(Boolean).length;
  if (remaining === 0 || hasAvailableMove(board)) {
    return { board, reshuffled: false };
  }

  for (let attempt = 0; attempt < 50; attempt += 1) {
    const candidate = shuffleExistingTiles(board);
    if (hasAvailableMove(candidate)) {
      return { board: candidate, reshuffled: true };
    }
  }

  return { board, reshuffled: false };
};

const generateSolvableBoard = () => {
  let board = createBoard();
  let attempt = 0;
  while (!hasAvailableMove(board) && attempt < 100) {
    board = createBoard();
    attempt += 1;
  }
  return board;
};

export default function LianLianKan() {
  const navigate = useNavigate();
  const [board, setBoard] = useState(() => generateSolvableBoard());
  const [selected, setSelected] = useState([]);
  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const [message, setMessage] = useState(defaultMessage);
  const [lastMismatch, setLastMismatch] = useState(null);
  const [hintPair, setHintPair] = useState(null);
  const [linkPath, setLinkPath] = useState(null);
  const messageTimer = useRef(null);
  const linkTimerRef = useRef(null);
  const boardWrapRef = useRef(null);
  const boardRef = useRef(null);
  const tileRefs = useRef(new Map());

  const totalPairs = useMemo(() => (ROWS * COLS) / 2, []);
  const progress = Math.round((matches / totalPairs) * 100);
  const finished = matches === totalPairs;

  const flashMessage = useCallback((text, duration = 1500) => {
    if (messageTimer.current) {
      clearTimeout(messageTimer.current);
      messageTimer.current = null;
    }
    setMessage(text);
    if (duration > 0) {
      messageTimer.current = setTimeout(() => {
        setMessage(defaultMessage);
        messageTimer.current = null;
      }, duration);
    }
  }, []);

  useEffect(() => () => {
    if (messageTimer.current) clearTimeout(messageTimer.current);
    if (linkTimerRef.current) clearTimeout(linkTimerRef.current);
  }, []);

  useEffect(() => {
    if (finished) {
      if (messageTimer.current) {
        clearTimeout(messageTimer.current);
        messageTimer.current = null;
      }
      setMessage('Board cleared! Hit reset to mix up the tiles.');
    }
  }, [finished]);

  const computeLinkGeometry = useCallback((pathNodes) => {
    if (!pathNodes || pathNodes.length < 2) return null;
    const boardEl = boardRef.current;
    const wrapEl = boardWrapRef.current;
    if (!boardEl || !wrapEl) return null;

    let sampleTile = null;
    outer: for (const row of board) {
      for (const cell of row) {
        if (!cell) continue;
        const el = tileRefs.current.get(cell.id);
        if (el) {
          sampleTile = el;
          break outer;
        }
      }
    }
    if (!sampleTile) return null;

    const tileRect = sampleTile.getBoundingClientRect();
    const tileWidth = tileRect.width;
    const tileHeight = tileRect.height;
    const styles = window.getComputedStyle(boardEl);
    const parseGap = (value) => {
      if (!value) return 0;
      const num = parseFloat(value);
      return Number.isNaN(num) ? 0 : num;
    };
    const gapX = parseGap(styles.columnGap || styles.gap);
    const gapY = parseGap(styles.rowGap || styles.gap);
    const wrapRect = wrapEl.getBoundingClientRect();
    const spacingX = tileWidth + gapX;
    const spacingY = tileHeight + gapY;
    const width = wrapRect.width;
    const height = wrapRect.height;
    const clampCoord = (val, min, max) => Math.min(max, Math.max(min, val));

    const points = pathNodes.map(({ r, c }) => {
      const gridRow = r - 1;
      const gridCol = c - 1;
      const rawX = gridCol * spacingX + tileWidth / 2;
      const rawY = gridRow * spacingY + tileHeight / 2;
      return {
        x: clampCoord(rawX, 0, width),
        y: clampCoord(rawY, 0, height),
      };
    });

    return { points, box: { width, height } };
  }, [board]);

  const showLinkLine = useCallback((pathNodes) => {
    const geometry = computeLinkGeometry(pathNodes);
    if (!geometry) return;
    setLinkPath(geometry);
    if (linkTimerRef.current) clearTimeout(linkTimerRef.current);
    linkTimerRef.current = setTimeout(() => setLinkPath(null), 450);
  }, [computeLinkGeometry]);

  const processPair = useCallback((pair) => {
    const [first, second] = pair;
    setLinkPath(null);
    const tileA = board[first.row][first.col];
    const tileB = board[second.row][second.col];
    if (!tileA || !tileB) {
      setTimeout(() => setSelected([]), 100);
      return;
    }

    setMoves((prev) => prev + 1);
    setHintPair(null);

    const pathNodes = tileA.value === tileB.value ? findLinkPath(board, first, second) : null;
    if (pathNodes) {
      showLinkLine(pathNodes);
      const updated = board.map((row, rIdx) =>
        row.map((cell, cIdx) => {
          if ((rIdx === first.row && cIdx === first.col) || (rIdx === second.row && cIdx === second.col)) {
            return null;
          }
          return cell;
        }),
      );
      const { board: ensuredBoard, reshuffled } = ensurePlayableBoard(updated);
      setBoard(ensuredBoard);
      setMatches((prev) => prev + 1);
      flashMessage(reshuffled ? 'Nice link! Tiles reshuffled to keep moves coming.' : 'Nice link! Keep the chain going.', 1500);
      setSelected([]);
      setLastMismatch(null);
      return;
    }

    setLastMismatch({ first, second });
    flashMessage('These two cannot see each other yet. Clear a path!', 1500);
    setTimeout(() => setSelected([]), 500);
  }, [board, flashMessage, showLinkLine]);

  const handleTileClick = (row, col) => {
    if (finished) return;
    if (selected.length === 2) return;
    const tile = board[row][col];
    if (!tile) return;
    setHintPair(null);
    if (selected.some((item) => item.row === row && item.col === col)) return;

    const nextSelection = [...selected, { row, col }];
    setSelected(nextSelection);
    if (nextSelection.length === 2) {
      processPair(nextSelection);
    }
  };

  const resetGame = () => {
    setBoard(generateSolvableBoard());
    setSelected([]);
    setMoves(0);
    setMatches(0);
    setLastMismatch(null);
    setHintPair(null);
    setLinkPath(null);
    flashMessage(defaultMessage, 0);
  };

  const showHint = () => {
    if (finished) return;
    const pair = findLinkablePair(board);
    if (pair) {
      setHintPair(pair);
      flashMessage('Hint highlighted! Try linking those tiles.', 1200);
      return;
    }

    const { board: ensuredBoard, reshuffled } = ensurePlayableBoard(board);
    setBoard(ensuredBoard);
    setHintPair(null);
    setLinkPath(null);
    if (reshuffled) {
      flashMessage('No moves left! Tiles reshuffled for you.', 1500);
    } else {
      flashMessage('No available moves right now.', 1500);
    }
  };

  return (
    <Box className="llk-shell">
      <Stack
        className="llk-header"
        direction={{ xs: 'column', md: 'row' }}
        alignItems="center"
        spacing={2}
        justifyContent="space-between"
      >
        <Button className="llk-ghost-btn" onClick={() => navigate('/Dashboard')} type="button" variant="outlined">
          Dashboard
        </Button>
        <Box className="llk-titleBlock">
          <Typography variant="h3" component="h1">连连看</Typography>
        </Box>
        <Stack className="llk-actions" direction="row" spacing={1}>
          <Button className="llk-hint-btn" type="button" onClick={showHint} variant="outlined">
            Show Hint
          </Button>
          <Button className="llk-action-btn" onClick={resetGame} type="button" variant="contained">
            Reset Board
          </Button>
        </Stack>
      </Stack>

      <Stack
        className="llk-stats"
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        justifyContent="center"
        alignItems="center"
      >
        <Paper sx={{ color: 'white' }} elevation={6} className="llk-statCard">
          <Typography className="llk-statLabel">Moves</Typography>
          <Typography className="llk-statValue">{moves}</Typography>
        </Paper>
        <Paper sx={{ color: 'white' }} elevation={6} className="llk-statCard">
          <Typography className="llk-statLabel">Matches</Typography>
          <Typography className="llk-statValue">{matches}/{totalPairs}</Typography>
        </Paper>
        <Paper sx={{ color: 'white' }} elevation={6} className="llk-statCard">
          <Typography className="llk-statLabel">Pairs Left</Typography>
          <Typography className="llk-statValue">{totalPairs - matches}</Typography>
        </Paper>
      </Stack>

      <Box className="llk-progress">
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 8,
            borderRadius: '999px',
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            '& .MuiLinearProgress-bar': {
              borderRadius: '999px',
              background: 'linear-gradient(90deg, #54ffce, #62b0ff)',
            },
          }}
        />
      </Box>

      <Typography className="llk-message">{message}</Typography>

      <Box className="llk-boardWrap" ref={boardWrapRef}>
        <Box className="llk-board" ref={boardRef} style={{ '--llk-cols': String(COLS) }}>
          {board.map((row, rowIdx) =>
            row.map((tile, colIdx) => {
              const key = tile ? tile.id : `empty-${rowIdx}-${colIdx}`;
              const isSelected = selected.some((item) => item.row === rowIdx && item.col === colIdx);
              const isMismatch = (lastMismatch?.first?.row === rowIdx && lastMismatch?.first?.col === colIdx) ||
                (lastMismatch?.second?.row === rowIdx && lastMismatch?.second?.col === colIdx);

              if (!tile) {
                return <Box key={key} className="llk-tile empty" aria-hidden="true" />;
              }

              let tileClass = 'llk-tile';
              if (isSelected) tileClass += ' selected';
              if (isMismatch) tileClass += ' mismatch';
              const isHint = (hintPair?.first?.row === rowIdx && hintPair?.first?.col === colIdx) ||
                (hintPair?.second?.row === rowIdx && hintPair?.second?.col === colIdx);
              if (isHint) tileClass += ' hint';

              return (
                <button
                  key={key}
                  type="button"
                  className={tileClass}
                  onClick={() => handleTileClick(rowIdx, colIdx)}
                  aria-label={`Tile ${tile.value}`}
                  ref={(el) => {
                    if (el) tileRefs.current.set(tile.id, el);
                    else tileRefs.current.delete(tile.id);
                  }}
                >
                  <span>{tile.value}</span>
                </button>
              );
            }),
          )}
        </Box>
        {linkPath?.points?.length >= 2 && (
          <svg
            className="llk-link-overlay"
            viewBox={`0 0 ${linkPath.box.width} ${linkPath.box.height}`}
            preserveAspectRatio="none"
          >
            <polyline points={linkPath.points.map((pt) => `${pt.x},${pt.y}`).join(' ')} />
          </svg>
        )}
      </Box>
    </Box>
  );
}
